import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGuidedPlanningContext } from '@/features/guided-financial-planning/context';

/** Removed step paths → redirect target */
const REMOVED_STEP_REDIRECTS: Record<string, string> = {
  'preferences-payment': 'preferences-benefit-structure',
  'options': 'quotes-loading',
  'preferences-premium': 'quotes-loading',
};

/**
 * Hook to sync react-use-wizard with react-router.
 * Returns startIndex and onStepChange props for the Wizard component.
 */
export function useWizardRouter() {
  const { flatStepConfigs } = useGuidedPlanningContext();
  const location = useLocation();
  const navigate = useNavigate();

  const pathSegments = location.pathname.split('/');
  const currentStepPath = pathSegments[pathSegments.length - 1] ?? '';

  const startIndex = useMemo(() => {
    const rawIndex = flatStepConfigs.findIndex(step => step.path === currentStepPath);
    if (rawIndex >= 0) return rawIndex;

    const redirect = REMOVED_STEP_REDIRECTS[currentStepPath];
    if (redirect) {
      const redirectIndex = flatStepConfigs.findIndex(step => step.path === redirect);
      if (redirectIndex >= 0) return redirectIndex;
    }

    return 0;
  }, [currentStepPath, flatStepConfigs]);

  // Redirect URL for removed steps so bookmarks/back-navigation land on a valid step
  useEffect(() => {
    const redirect = REMOVED_STEP_REDIRECTS[currentStepPath];
    if (redirect && !flatStepConfigs.some(s => s.path === currentStepPath)) {
      const target = flatStepConfigs.find(s => s.path === redirect);
      if (target) {
        const newPath = [...pathSegments.slice(0, -1), target.path].join('/');
        navigate({ pathname: newPath, search: location.search }, { replace: true });
      }
    }
  }, [currentStepPath, flatStepConfigs, navigate, location.search, pathSegments]);

  const onStepChange = useCallback(
    (stepIndex: number) => {
      const stepPath = flatStepConfigs[stepIndex]?.path;
      if (!stepPath) return;

      const segments = location.pathname.split('/');
      const lastSegment = segments[segments.length - 1];
      const isValidStep = flatStepConfigs.some(s => s.path === lastSegment);

      const newPath = isValidStep
        ? [...segments.slice(0, -1), stepPath].join('/')
        : `${location.pathname}/${stepPath}`;

      const currentStepConfig = flatStepConfigs.find(s => s.path === lastSegment);
      const shouldReplace = currentStepConfig?.skipHistory ?? false;

      navigate({ pathname: newPath, search: location.search }, { replace: shouldReplace });
    },
    [flatStepConfigs, location.pathname, location.search, navigate],
  );

  return { startIndex, onStepChange };
}
